import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { interactionTypeRootPathName } from '@/helpers';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { coreApi } from '$api-clients/clients';
import { useCore } from '$api-clients/hooks';
import type { InteractionInformation } from '../../components/NeedsAttention/types';
import { buildClientNames } from '../useListingAgreements/useListingAgreements.helpers';
import { getEnvelopeRequestQueryParams } from '../useNeedsAttention/helpers';
import {
  getEnvelopeDetails,
  getInteractionHeader,
} from './useEnvelopesNeedsReminders.helpers';

const PAGE_SIZE = 10;

export const useEnvelopesNeedsReminders = ({ skip }: { skip: boolean }) => {
  const router = useRouter();
  const trackEvent = useTrackHomePageAnalytics();
  const [currentPage, setCurrentPage] = useState(1);
  const [interactions, setInteractions] = useState<
    Record<number, InteractionInformation[]>
  >({});
  // The per-interaction envelope details are fetched after the page of
  // interactions resolves, so track that work separately from the SWR load.
  const [isFormatting, setIsFormatting] = useState(false);

  useEffect(() => {
    if (skip) {
      // When the modal closes (skip becomes true), reset to page 1 so the next
      // open refetches from the first page rather than just the last one viewed.
      setCurrentPage(1);
    }
  }, [skip]);

  const {
    interactionStatusFacet,
    envelopeStatusFacet,
    envelopeSentAtFacet,
    envelopeReminderSentAtFacet,
  } = getEnvelopeRequestQueryParams();

  const { data, isLoading, error } = useCore(
    '/interactions',
    skip
      ? null
      : {
          params: {
            query: {
              sort: 'sentAt:asc',
              facets: `${interactionStatusFacet},${envelopeStatusFacet},${envelopeSentAtFacet},${envelopeReminderSentAtFacet}`,
              includeContacts: true,
              offset: (currentPage - 1) * PAGE_SIZE,
              limit: PAGE_SIZE,
            },
          },
        },
  );

  useEffect(() => {
    if (error) {
      datadogRum.addError(error, {
        context: 'useEnvelopesNeedsReminders: failed to fetch interactions',
      });
    }
  }, [error]);

  useEffect(() => {
    if (!data) return;

    let cancelled = false;
    setIsFormatting(true);

    const pageInteractions = data.results.map((result) => result.interaction);

    void Promise.all(
      pageInteractions.map(async (interaction) => {
        try {
          const { data: envelopes, error: envelopesError } = await coreApi.GET(
            '/interactions/{interactionId}/envelopes',
            {
              params: {
                path: { interactionId: interaction.id },
                query: { status: 'sent', needsAttention: true },
              },
            },
          );

          if (envelopesError) {
            throw new Error(
              typeof envelopesError === 'string'
                ? envelopesError
                : envelopesError.message,
            );
          }

          return { interaction, envelopes: envelopes ?? [] };
        } catch (err) {
          // A single failed envelope lookup shouldn't drop the whole page;
          // degrade that card to no envelope detail instead.
          datadogRum.addError(err, {
            context: 'useEnvelopesNeedsReminders: failed to fetch envelopes',
            interactionId: interaction.id,
          });
          return { interaction, envelopes: [] };
        }
      }),
    )
      .then((withEnvelopes) => {
        if (cancelled) return;

        const formattedInteractions = withEnvelopes.map(
          ({ interaction, envelopes }, index) => ({
            subtitle: buildClientNames(interaction.contacts ?? []),
            title: getInteractionHeader(interaction),
            description: getEnvelopeDetails(envelopes),
            id: interaction.id,
            elementId: `envelope-needs-reminder-${index}`,
            onClick: (numOrder: number) => {
              trackEvent(
                TrackHomePageEvent.NeedsAttentionModalInteractionClick,
                {
                  interactionId: interaction.id,
                  numOrder,
                },
              );
              void router.push(
                `/${interactionTypeRootPathName(interaction.type)}/${interaction.id}/documents?utm_source=hp&utm_medium=nac`,
              );
            },
          }),
        );

        setInteractions((prev) => ({
          ...prev,
          [currentPage]: formattedInteractions,
        }));
      })
      .finally(() => {
        if (!cancelled) setIsFormatting(false);
      });

    return () => {
      cancelled = true;
    };
  }, [data, currentPage, router, trackEvent]);

  const handleLoadMore = () => {
    if (isLoading || isFormatting || !data?.next) return;
    setCurrentPage((prev) => prev + 1);
  };

  return {
    envelopeInteractions: Object.values(interactions).flat(),
    loading: isLoading || isFormatting,
    onLoadMore: handleLoadMore,
  };
};
